import type { SearchActionRow } from "~/helpers/search/deriveSearchActionRows";
import type { SearchResultRow } from "~/layout/useEntitySearch";

import { Box, Divider, IconButton, Paper, Popper, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import FernieMicro from "~/components/fernie/FernieMicro";
import FernieName from "~/components/fernie/FernieName";
import { fernieHoverGlowSx } from "~/components/fernie/fernieHoverGlow";
import ShortcutHintBadge from "~/components/keyboard/ShortcutHintBadge";
import { CaptionText } from "~/components/Typographies";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import parseEntitySearch, { replaceEntityQuery } from "~/helpers/search/entitySearchQuery";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import useDebounce from "~/hooks/useDebounce";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useHasChatHistory from "~/hooks/useHasChatHistory";
import GlobalSearchField from "~/layout/GlobalSearchField";
import SearchResultsList from "~/layout/SearchResultsList";
import runGuardedExit from "~/layout/unsavedChangesRegistry";
import useCommandCatalog from "~/layout/useCommandCatalog";
import useGlobalSearch, { GLOBAL_SEARCH_TYPING_DELAY_MS } from "~/layout/useGlobalSearch";
import { toggleAiPanel, updateGridSearchText } from "~/store/layoutSlice";
import { fernieColors } from "~/themes/fernieBrand";
import { TOUR_ANCHORS } from "~/walkthroughs/registry";

const LISTBOX_ID = "global-search-listbox";
const optionId = (index: number) => `${LISTBOX_ID}-option-${index}`;

const SYNTAX_HINTS: { key: string; labelKey: string }[] = [
	{ key: "o:", labelKey: "search.hints.orders" },
	{ key: "c:", labelKey: "search.hints.customers" },
	{ key: "i:", labelKey: "search.hints.items" },
	{ key: ">", labelKey: "search.hints.commands" }
];

const GlobalSearch = () => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const gridSearchText = useAppSelector((state) => state.layout.gridSearchText);
	const aiPanelOpen = useAppSelector((state) => state.layout.aiPanelOpen);
	const { isEnabled: isAiChatEnabled } = useFeatureFlag(FeatureFlagName.AiChat);
	const hasChatHistory = useHasChatHistory();

	const inputRef = useRef<HTMLInputElement>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [focused, setFocused] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const [inputValue, setInputValue] = useState(gridSearchText);
	const dispatchedValueRef = useRef(gridSearchText);

	const debouncedValue = useDebounce(inputValue, GLOBAL_SEARCH_TYPING_DELAY_MS);

	useEffect(() => {
		const gridValue = parseEntitySearch(debouncedValue).explicit ? "" : debouncedValue;
		if (gridValue === dispatchedValueRef.current) {
			return;
		}
		dispatchedValueRef.current = gridValue;
		dispatch(updateGridSearchText(gridValue));
	}, [debouncedValue]);

	useEffect(() => {
		if (gridSearchText === dispatchedValueRef.current) {
			return;
		}
		dispatchedValueRef.current = gridSearchText;
		setInputValue(gridSearchText);
	}, [gridSearchText]);

	const commands = useCommandCatalog();
	const { actionRows, hasActiveGrid, intent, isLoadingResults, itemSuggestion, navigableRows, query, sections } =
		useGlobalSearch(inputValue, focused, commands);

	const open = focused;
	const hasActiveOption = open && navigableRows.length > 0;

	const resetSearch = () => {
		setInputValue("");
		dispatchedValueRef.current = "";
		dispatch(updateGridSearchText(""));
		setActiveIndex(0);
	};

	const go = (row: SearchResultRow) => {
		resetSearch();
		setFocused(false);
		if (row.run) {
			inputRef.current?.blur();
			row.run();
			return;
		}
		runGuardedExit(() => navigate(row.path, row.state ? { state: row.state } : undefined));
	};

	const applyFilterToGrid = () => {
		const term = inputValue.trim();
		if (!term || parseEntitySearch(inputValue).explicit) {
			return;
		}
		dispatchedValueRef.current = inputValue;
		dispatch(updateGridSearchText(inputValue));
		setFocused(false);
		inputRef.current?.blur();
	};

	const runSuggestion = (suggestion: string) => {
		setInputValue(replaceEntityQuery(inputValue, intent, suggestion));
		setActiveIndex(0);
	};

	const runActionRow = (row: SearchActionRow) => {
		if (row.kind === "filterGrid") {
			applyFilterToGrid();
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Escape") {
			event.preventDefault();
			resetSearch();
			setFocused(false);
			inputRef.current?.blur();
			return;
		}
		if (event.key === "Enter" && event.altKey) {
			event.preventDefault();
			if (hasActiveGrid) {
				applyFilterToGrid();
			}
			return;
		}
		if (!open || navigableRows.length === 0) {
			return;
		}
		if (event.key === "ArrowDown") {
			event.preventDefault();
			setActiveIndex((index) => (index + 1) % navigableRows.length);
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setActiveIndex((index) => (index - 1 + navigableRows.length) % navigableRows.length);
		} else if (event.key === "Enter") {
			event.preventDefault();
			go(navigableRows[activeIndex] ?? navigableRows[0]);
		}
	};

	const placeholder = hasActiveGrid ? t("search.placeholderGrid") : t("search.placeholder");

	return (
		<GlobalSearchField
			anchorRef={setAnchorEl}
			inputProps={{
				"aria-activedescendant": hasActiveOption ? optionId(activeIndex) : undefined,
				"aria-autocomplete": "list",
				"aria-controls": open ? LISTBOX_ID : undefined,
				"aria-expanded": open,
				"aria-label": t("search.inputAria"),
				role: "combobox"
			}}
			inputRef={inputRef}
			onBlur={() => window.setTimeout(() => setFocused(false), 120)}
			onChange={(value) => {
				setInputValue(value);
				setActiveIndex(0);
			}}
			onClear={resetSearch}
			onFocus={() => setFocused(true)}
			onKeyDown={handleKeyDown}
			placeholder={placeholder}
			tourId={TOUR_ANCHORS.globalSearch}
			value={inputValue}
			width="min(440px, 38vw)"
		>
			{isAiChatEnabled && (
				<>
					<Divider flexItem orientation="vertical" sx={{ my: 1 }} />
					<Box sx={{ display: "inline-flex", position: "relative" }}>
						<Tooltip title={hasChatHistory ? t("search.fernieWithHistory") : t("search.fernie")}>
							<IconButton
								aria-label={t("search.toggleFernieAria")}
								aria-pressed={aiPanelOpen}
								onClick={() => dispatch(toggleAiPanel())}
								size="small"
								sx={{ ...fernieHoverGlowSx, borderRadius: 100, gap: 0.5, px: 0.75, py: 0.25 }}
							>
								<FernieMicro size={20} variant="smile" />
								<FernieName color={aiPanelOpen ? fernieColors.fern : fernieColors.tile} fontSize={13} trademark />
							</IconButton>
						</Tooltip>
						<ShortcutHintBadge actionId="toggleAiPanel" placement="bottom-right" />
					</Box>
				</>
			)}
			<Popper anchorEl={anchorEl} open={open} placement="bottom-start" sx={{ zIndex: (theme) => theme.zIndex.modal }}>
				<Paper
					onMouseDown={(event) => event.preventDefault()}
					sx={{ maxHeight: 400, mt: 0.5, overflowX: "hidden", overflowY: "auto", width: anchorEl?.clientWidth ?? 320 }}
					variant="outlined"
				>
					{!query && (
						<Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1, px: 2, py: 1 }}>
							{SYNTAX_HINTS.map((hint) => (
								<Box key={hint.key} sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
									<Box
										component="span"
										sx={{
											bgcolor: "action.hover",
											border: 1,
											borderColor: "divider",
											borderRadius: 0.75,
											fontFamily: "monospace",
											fontSize: "0.7rem",
											lineHeight: 1.4,
											px: 0.5
										}}
									>
										{hint.key}
									</Box>
									<CaptionText sx={{ color: "text.secondary" }}>{t(hint.labelKey)}</CaptionText>
								</Box>
							))}
						</Box>
					)}
					<SearchResultsList
						actionRows={actionRows}
						activeIndex={activeIndex}
						getOptionId={optionId}
						intent={intent}
						isLoadingResults={isLoadingResults}
						itemSuggestion={itemSuggestion}
						listboxId={LISTBOX_ID}
						navigableRows={navigableRows}
						onRunAction={runActionRow}
						onSelectRow={go}
						onSuggestionSelect={runSuggestion}
						query={query}
						sections={sections}
						showActionKeyHint
					/>
				</Paper>
			</Popper>
		</GlobalSearchField>
	);
};

export default GlobalSearch;
